export type DateInput = string | number | Date | null | undefined;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DISPLAY_DATE_PATTERN = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/;
const DATE_TOKEN_PATTERN = /\d{4}-\d{2}-\d{2}|\d{1,2}[./]\d{1,2}[./]\d{4}/g;

const pad = (value: number) => value.toString().padStart(2, '0');

const isDateLike = (value: unknown): value is Date =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as Date).getTime === 'function' &&
  typeof (value as Date).getDate === 'function';

const formatHour12 = (hours: number) => {
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const normalizedHours = hours % 12 || 12;
  return { hours: pad(normalizedHours), suffix };
};

export const formatDisplayDate = (value: DateInput, fallback = '-'): string => {
  const date = parseDateFlexible(value);
  if (!date) return fallback;

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

export const formatDisplayDateTime = (
  value: DateInput,
  options?: {
    fallback?: string;
    includeSeconds?: boolean;
    hour12?: boolean;
  }
): string => {
  const date = parseDateFlexible(value);
  const fallback = options?.fallback ?? '-';

  if (!date) return fallback;

  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const datePart = formatDisplayDate(date, fallback);

  if (options?.hour12) {
    const { hours, suffix } = formatHour12(date.getHours());
    const timePart = options.includeSeconds
      ? `${hours}:${minutes}:${seconds} ${suffix}`
      : `${hours}:${minutes} ${suffix}`;
    return `${datePart} ${timePart}`;
  }

  const timePart = options?.includeSeconds
    ? `${pad(date.getHours())}:${minutes}:${seconds}`
    : `${pad(date.getHours())}:${minutes}`;

  return `${datePart} ${timePart}`;
};

export const formatDisplayDateRange = (
  value: string | null | undefined,
  fallback = '-'
): string => {
  if (!value) return fallback;

  const trimmedValue = value.trim();
  if (!trimmedValue) return fallback;

  const formattedValue = trimmedValue.replace(DATE_TOKEN_PATTERN, (match) =>
    formatDisplayDate(match, match)
  );

  return formattedValue || fallback;
};

export const formatDateWithOptions = (
  value: DateInput,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US',
  fallback = '-'
): string => {
  const date = parseDateFlexible(value);
  if (!date) return fallback;

  return new Intl.DateTimeFormat(locale, options).format(date);
};

export const formatIsoDate = (isoDate: string, fallback = '-') =>
  formatDisplayDate(isoDate, fallback);

/** Calendar fields from react-multi-date-picker DateObject (no timezone conversion). */
export type PickerCalendarDate = {
  year: number;
  month: { number: number };
  day: number;
};

/** Format a picker value as DD/MM/YYYY using calendar year/month/day when available. */
export const formatPickerCalendarDate = (
  value: PickerCalendarDate | string | null | undefined
): string | undefined => {
  if (!value) return undefined;

  if (typeof value === 'string') {
    const formatted = formatDisplayDate(value, '');
    return formatted || undefined;
  }

  if (
    typeof value.year === 'number' &&
    typeof value.day === 'number' &&
    typeof value.month?.number === 'number'
  ) {
    return `${pad(value.day)}/${pad(value.month.number)}/${value.year}`;
  }

  return undefined;
};

// Parse a date string that may be either a date-only (YYYY-MM-DD) or a full ISO datetime.
// Ensures date-only strings are interpreted in local time (midnight) instead of UTC to avoid off-by-one day shifts.
// Returns a Date instance (caller can extract timestamp or normalize further if needed).
export const parseDateFlexible = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;

  if (isDateLike(value)) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) return null;

  if (DATE_ONLY_PATTERN.test(normalizedValue)) {
    const [y, m, d] = normalizedValue.split('-').map(Number);
    return new Date(y, m - 1, d); // local midnight
  }

  const displayDateMatch = normalizedValue.match(DISPLAY_DATE_PATTERN);
  if (displayDateMatch) {
    const [, d, m, y] = displayDateMatch.map(Number);
    return new Date(y, m - 1, d);
  }

  const dt = new Date(normalizedValue);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};
